"use client";

import {
  CHART_COLORS,
  CHART_LINE_CURVE_TYPES,
  formatChartDetailDisplayValue,
  formatChartDisplayValue,
} from "@lattice/shared-frontend/charts";
import React, { useMemo } from "react";
import {
  Line,
  LineChart as RechartsLineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import type { T_ChartData } from "@lattice/core-lib/types/chart";

import { ChartSeriesHeader } from "./ChartSeriesHeader";
import { CHART_X_AXIS_MIN_TICK_GAP } from "./chart-axis";
import { getPointLabel, getPointValue, isScanEligible } from "./chart-data";
import { resolveHeaderIndex } from "./chart-scan";
import { useChartScan } from "./use-chart-scan";
import "./charts-kit.css";

const AXIS_FONT_SIZE = 10;

export type T_MultiLineChartProps = {
  chart: T_ChartData;
  height?: number;
  syntheticFallback?: boolean;
};

/**
 * Multi-series line chart — web mirror of the mobile InlineChartV2
 * multi-line renderer. Every series gets a legend entry with a live value;
 * the legend IS the scan readout (index-keyed via useChartScan).
 */
export function MultiLineChart({
  chart,
  height = 220,
  syntheticFallback,
}: T_MultiLineChartProps) {
  const formatOptions = {
    valueFormat: chart.valueFormat,
    valuePrefix: chart.valuePrefix,
    valueSuffix: chart.valueSuffix,
  };
  const seriesNames = useMemo(
    () => (chart.series && chart.series.length > 0 ? chart.series : ["value"]),
    [chart.series],
  );
  const points = useMemo(
    () =>
      chart.data.map((point, index) => {
        const row: Record<string, number | string | null> = {
          index,
          label: getPointLabel(point),
        };
        for (const name of seriesNames) {
          row[name] = getPointValue(point, name);
        }
        return row;
      }),
    [chart.data, seriesNames],
  );
  const scanEnabled = isScanEligible(chart, syntheticFallback);
  const { handlers, scanIndex } = useChartScan(points.length, scanEnabled);
  const headerIndex = resolveHeaderIndex(scanIndex, points.length);
  const headerRow = headerIndex === null ? null : points[headerIndex];
  const hasNegative = points.some((row) =>
    seriesNames.some((name) => {
      const value = row[name];
      return typeof value === "number" && value < 0;
    }),
  );

  if (points.length === 0) {
    return (
      <div className="ck-root">
        <div className="ck-empty">No chart data</div>
      </div>
    );
  }

  return (
    <div className="ck-root">
      {chart.title ? (
        <div className="ck-title-row">
          <h3 className="ck-title">{chart.title}</h3>
        </div>
      ) : null}

      {headerRow ? (
        <ChartSeriesHeader
          entries={seriesNames.map((name, seriesIndex) => {
            const value = headerRow[name];
            return {
              color: CHART_COLORS[seriesIndex % CHART_COLORS.length],
              name,
              value:
                typeof value === "number"
                  ? formatChartDetailDisplayValue(value, formatOptions)
                  : "—",
            };
          })}
          label={typeof headerRow.label === "string" ? headerRow.label : null}
        />
      ) : null}

      <div
        className={scanEnabled ? "ck-plot ck-plot--scan" : "ck-plot"}
        style={{ height }}
      >
        <ResponsiveContainer height="100%" width="100%">
          <RechartsLineChart
            data={points}
            margin={{ bottom: 4, left: 0, right: 8, top: 8 }}
            {...handlers}
          >
            <XAxis
              axisLine={false}
              // Index-keyed axis: see LineChart.tsx — ReferenceLine needs a
              // numeric domain and label-addressing is policy-banned.
              dataKey="index"
              interval="preserveStartEnd"
              minTickGap={CHART_X_AXIS_MIN_TICK_GAP}
              stroke="var(--lattice-chart-axis-label)"
              tick={{ fontSize: AXIS_FONT_SIZE }}
              tickFormatter={(value: number) =>
                String(points[value]?.label ?? "")
              }
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              domain={["auto", "auto"]}
              stroke="var(--lattice-chart-axis-label)"
              tick={{ fontSize: AXIS_FONT_SIZE }}
              tickFormatter={(value: number) =>
                formatChartDisplayValue(value, formatOptions)
              }
              tickLine={false}
              width={44}
            />
            {hasNegative ? (
              <ReferenceLine
                stroke="var(--lattice-chart-zero-line)"
                strokeWidth={1}
                y={0}
              />
            ) : null}
            {scanIndex !== null ? (
              <ReferenceLine
                stroke="var(--lattice-chart-zero-line)"
                strokeWidth={1.4}
                x={scanIndex}
              />
            ) : null}
            {seriesNames.map((name, seriesIndex) => (
              <Line
                activeDot={false}
                dataKey={name}
                dot={false}
                isAnimationActive={false}
                key={name}
                stroke={CHART_COLORS[seriesIndex % CHART_COLORS.length]}
                strokeWidth={2}
                type={CHART_LINE_CURVE_TYPES.web}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
